import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import Loader from "../../components/Loader";
import { useFetchApi } from "../../hooks/useFetchApi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import routes from "../../setup/routes";
import Button from "../../components/Button";
import AddEditModal from "./AddEditModal";
import BulkUploadModal from "./BulkUploadModal";
import ButtonWithDropdown, {
  OptionTypes,
} from "../../components/ButtonWithDropdown";
import DeleteModal from "./DeleteModal";
import { captialCase } from "../../helper";
import { DispatchContext, StateContext } from "../../store/context";
import { UPDATE_ALERT, UPDATE_SELECTED_MARKET_ID } from "../../store/types";
import API from "../../network";
import { isAllowed } from "../../components/Entitlements";
import { KEYS } from "../../setup/entitlements";
import { Table } from "../../components/table";
import type { ColumnsType } from "antd/es/table";
import { useExportToExcel } from "../../hooks/useExportToExcel";

// Define the Stock type
interface Stock {
  id: number;
  sector_name: string;
  company_name: string;
  ticker_symbol: string;
  open: number;
  high: number;
  low: number;
  closed: number;
  last_traded_date: string;
  falcon_fiscal_year?: number | null;
  falcon_latest_quarter_date?: string | null;
  falcon_latest_quarter_label?: string | null;
  falcon_finalized_at?: string | null;
  latest_revenue_year?: number | null;
  latest_revenue_quarter?: number | null;
  latest_revenue_date?: string | null;
}

const STOCK_SUMMARY_ENDPOINTS = {
  ai: { url: "/api/ai-reviewed-summary", label: "AI summary" },
  beginner: { url: "/api/beginner-summary", label: "Beginner summary" },
} as const;

type StockSummaryKind = keyof typeof STOCK_SUMMARY_ENDPOINTS;

// Progress of a market-wide summary run, from GET /api/summary-jobs.
interface SummaryJob {
  kind: StockSummaryKind;
  stock_market_id: number;
  status: "running" | "completed";
  total: number;
  processed: number;
  saved: number;
  failed: number;
  current_stock: string | null;
  started_at: string;
  elapsed_seconds: number;
  estimated_seconds_per_stock: number;
  failures: { stock_id: number; stage: string; message: string }[];
}

const SUMMARY_JOB_LABELS: Record<StockSummaryKind, string> = {
  ai: "AI summaries",
  beginner: "Beginner summaries",
};

const SUMMARY_JOB_POLL_MS = 5000;

// role_id 1 = admin, 4 = senior.
const ADMIN_ROLE_ID = 1;
const SUMMARY_GENERATOR_ROLE_IDS = [ADMIN_ROLE_ID, 4];

const formatDuration = (seconds: number) => {
  if (seconds < 60) return "under a minute";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return hours ? `${hours} h ${minutes} min` : `${minutes} min`;
};

const StockMarketData: React.FC = (): React.ReactElement => {
  const location = useLocation();
  const {
    selectedMarketId,
    authUser: { role_name, role_id, allowed = [] },
  } = useContext(StateContext);
  // Per-row summary generation is for admins and seniors; market-wide runs
  // (and their progress panel) are admin-only.
  const canGenerateSummaries = SUMMARY_GENERATOR_ROLE_IDS.includes(
    Number(role_id),
  );
  const canGenerateMarketSummaries = Number(role_id) === ADMIN_ROLE_ID;
  console.log("selectedMarketId", selectedMarketId);
  const [selectedData, setEditData] = useState<any>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openModal, toggleModal] = useState<any>({
    new: false,
    edit: false,
    delete: false,
    stocks: false,
    "gross-profits": false,
    "net-profits": false,
    revenues: false,
    "pe-valuations": false,
  });
  const [filteredStocks, setFilteredStock] = useState<any>([]);
  const [selectedMarket, setSelectedMarket] = useState<number | string>(
    selectedMarketId,
  );
  const [bulkUploadName, setUploadName] = useState<string>("");
  const [opts, setOpts] = useState<Array<OptionTypes>>([]);

  const dispatch = useContext(DispatchContext);

  const {
    response: stocks,
    loading: stockLoading,
    execute: getStocks,
  } = useFetchApi("/api/stocks");

  const {
    response: stockMarkets,
    loading: stockMarketLoading,
    execute: getSockMarkets,
  } = useFetchApi("/api/stock-markets", {}, "GET");

  const {
    execute: getOtherMetrics,
    response: metrics = [],
    loading: metricsLoading,
  } = useFetchApi(`/api/metrics?hasTable=true`, {}, "GET");

  const {
    execute: getSectors,
    response: sectors,
    loading: sectorsLoading,
  } = useFetchApi(`/api/sectors`, {}, "GET");

  const {
    execute: getCompanyQuarters,
    response: quarters,
    loading: quartersLoading,
  } = useFetchApi(`/api/company_quarters`, {}, "GET");

  const { execute: generateAISummaries, loading: aiSummaryLoading } =
    useFetchApi("/api/ai-reviewed-summary", {}, "POST", () =>
      refreshSummaryJobs(),
    );
  // Stock currently generating, per summary kind (one of each at a time).
  const [summaryStockIds, setSummaryStockIds] = useState<
    Record<StockSummaryKind, number | null>
  >({ ai: null, beginner: null });

  // Waits for the summary to finish so the real outcome can be shown. Uses API
  // directly because useFetchApi drops the body of error (4xx/5xx) responses.
  const generateStockSummary = async (
    kind: StockSummaryKind,
    stockId: number,
  ) => {
    const { url, label } = STOCK_SUMMARY_ENDPOINTS[kind];
    setSummaryStockIds((ids) => ({ ...ids, [kind]: stockId }));
    dispatch({ type: UPDATE_ALERT, payload: null });
    try {
      const response = await API(url, {
        method: "POST",
        data: { stock_id: stockId, await_completion: true },
        headers: {
          Authorization: localStorage.getItem("token") || "",
          role_id,
        },
        validateStatus: () => true,
      });
      const { status, message } = response.data || {};
      const succeeded = response.status === 200 && status === "success";
      dispatch({
        type: UPDATE_ALERT,
        payload: {
          type: succeeded ? "success" : "error",
          message:
            message ||
            (succeeded
              ? `${label} saved for stock ${stockId}.`
              : `${label} failed for stock ${stockId} (HTTP ${response.status}).`),
        },
      });
    } catch (error: any) {
      dispatch({
        type: UPDATE_ALERT,
        payload: {
          type: "error",
          message: `${label} failed for stock ${stockId}: ${error?.message || "network error"}`,
        },
      });
    } finally {
      setSummaryStockIds((ids) => ({ ...ids, [kind]: null }));
    }
  };

  const renderSummaryButton = (kind: StockSummaryKind, stock: Stock) => (
    <div className="flex text-white justify-center">
      <Button
        variant="primary"
        fontSize="small"
        disabled={summaryStockIds[kind] !== null}
        onClick={() => generateStockSummary(kind, stock.id)}
      >
        {summaryStockIds[kind] === stock.id ? "Generating..." : "Generate"}
      </Button>
    </div>
  );
  const {
    execute: generateBeginnerSummaries,
    loading: beginnerSummaryLoading,
  } = useFetchApi("/api/beginner-summary", {}, "POST", () =>
    refreshSummaryJobs(),
  );

  const [summaryJobs, setSummaryJobs] = useState<SummaryJob[]>([]);
  const [dismissedJobKeys, setDismissedJobKeys] = useState<string[]>([]);
  const selectedMarketRef = useRef(selectedMarket);
  selectedMarketRef.current = selectedMarket;

  const summaryJobKey = (job: SummaryJob) => `${job.kind}:${job.started_at}`;

  // Uses API directly: useFetchApi clears the page alert on every call, which
  // would wipe notifications on each poll.
  const refreshSummaryJobs = async () => {
    const marketId = selectedMarketRef.current;
    if (!marketId || !canGenerateMarketSummaries) return;
    try {
      const response = await API(
        `/api/summary-jobs?stock_market_id=${marketId}`,
        {
          method: "GET",
          headers: {
            Authorization: localStorage.getItem("token") || "",
            role_id,
          },
        },
      );
      // Ignore a late response for a market the user has already left.
      if (marketId !== selectedMarketRef.current) return;
      setSummaryJobs(response.data?.data || []);
    } catch (error) {
      // Progress is informational; a failed poll just retries next tick.
      console.log("summary job status error", error);
    }
  };

  const getSummaryJob = (kind: StockSummaryKind) =>
    summaryJobs.find((job) => job.kind === kind);
  const isSummaryJobRunning = (kind: StockSummaryKind) =>
    getSummaryJob(kind)?.status === "running";
  const hasRunningSummaryJob = summaryJobs.some(
    (job) => job.status === "running",
  );

  useEffect(() => {
    setSummaryJobs([]);
    refreshSummaryJobs();
  }, [selectedMarket]);

  useEffect(() => {
    if (!hasRunningSummaryJob) return;
    const timer = setInterval(refreshSummaryJobs, SUMMARY_JOB_POLL_MS);
    return () => clearInterval(timer);
  }, [hasRunningSummaryJob, selectedMarket]);

  const summaryJobButtonLabel = (
    kind: StockSummaryKind,
    loading: boolean,
    idleLabel: string,
  ) => {
    const job = getSummaryJob(kind);
    if (job?.status === "running") {
      return `Generating... ${job.processed}/${job.total}`;
    }
    return loading ? "Starting..." : idleLabel;
  };

  const renderSummaryJobProgress = (job: SummaryJob) => {
    const running = job.status === "running";
    const percent = job.total
      ? Math.round((job.processed / job.total) * 100)
      : 0;
    // Use the observed pace once stocks finish; until then, the server's guess.
    const secondsPerStock = job.processed
      ? job.elapsed_seconds / job.processed
      : job.estimated_seconds_per_stock;
    const remainingSeconds = Math.round(
      (job.total - job.processed) * secondsPerStock,
    );
    const tone = running
      ? "border-blue-200 bg-blue-50"
      : job.failed
        ? "border-amber-200 bg-amber-50"
        : "border-green-200 bg-green-50";
    const barColor = running
      ? "bg-blue-600"
      : job.failed
        ? "bg-amber-500"
        : "bg-green-600";

    return (
      <div
        key={summaryJobKey(job)}
        className={`rounded-lg border p-3 text-sm text-gray-800 ${tone}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold">
            {SUMMARY_JOB_LABELS[job.kind]}
            {running ? " — processing" : " — completed"}
          </span>
          {!running && (
            <button
              type="button"
              aria-label="Dismiss"
              className="px-2 text-gray-500 hover:text-gray-800"
              onClick={() =>
                setDismissedJobKeys((keys) => [...keys, summaryJobKey(job)])
              }
            >
              ×
            </button>
          )}
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white">
          <div
            className={`h-2 rounded-full transition-all ${barColor}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="mt-1">
          {job.processed} of {job.total} stocks ({percent}%) · {job.saved} saved
          {job.failed ? `, ${job.failed} failed` : ""}
          {running
            ? ` · about ${formatDuration(remainingSeconds)} remaining`
            : ` · took ${formatDuration(job.elapsed_seconds)}`}
        </div>
        {running && job.current_stock && (
          <div className="text-gray-500">
            Now processing: {job.current_stock}
          </div>
        )}
        {job.failures.length > 0 && (
          <details className="mt-1">
            <summary className="cursor-pointer text-gray-600">
              Show failures
              {job.failed > job.failures.length ? " (latest)" : ""}
            </summary>
            <ul className="mt-1 list-disc pl-5 text-gray-600">
              {job.failures.map((failure) => (
                <li key={`${failure.stock_id}-${failure.stage}`}>
                  Stock {failure.stock_id} at {failure.stage}: {failure.message}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    );
  };

  const visibleSummaryJobs = summaryJobs.filter(
    (job) => !dismissedJobKeys.includes(summaryJobKey(job)),
  );

  const navigate = useNavigate();

  const isBulkUpload =
    Object.keys(openModal).filter((m: any) =>
      openModal[m] === true && !["new", "edit", "delete"].includes(m)
        ? openModal[m]
        : false,
    ).length > 0;

  const toggle = (e: any, market?: any) => {
    const type = e.target.id;
    console.log("ty", type);
    setUploadName(type);
    if (market && Object.keys(market).length) {
      setEditData(market);
    }
    if (type) {
      if (openModal[type]) {
        setEditData({});
      }
      toggleModal({ ...openModal, [type]: !openModal[type] });
    }
  };

  const handleView = (e: any) => {
    const id = e.target.id;
    navigate(`${routes.DASHBOARD}/${routes.STOCKS.replace(":id", id)}`, {
      state: { selectedMarket },
    });
  };

  const searchFilteredStocks = useMemo(() => {
    if (!filteredStocks || !searchTerm.trim()) {
      return filteredStocks || [];
    }
    const searchTermLower = searchTerm.toLowerCase();
    return filteredStocks.filter(
      (stock: Stock) =>
        stock.company_name.toLowerCase().includes(searchTermLower) ||
        stock.ticker_symbol.toLowerCase().includes(searchTermLower),
    );
  }, [filteredStocks, searchTerm]);

  const clearSearch = () => {
    setSearchTerm("");
  };

  const renderFalconStatus = (stock: Stock) => {
    if (!stock?.falcon_fiscal_year) {
      return <span className="text-gray-400">-</span>;
    }

    return (
      <div className="flex flex-col gap-1">
        <span className="inline-flex w-fit items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
          Falcon
        </span>
        <span className="text-xs text-gray-600">
          FY {stock.falcon_fiscal_year}
          {stock.falcon_latest_quarter_date
            ? ` | ${stock.falcon_latest_quarter_date}`
            : ""}
        </span>
      </div>
    );
  };

  const renderLatestQuarter = (stock: Stock) => {
    if (!stock?.latest_revenue_year) {
      return <span className="text-gray-400">-</span>;
    }

    return (
      <div className="flex flex-col gap-1">
        <span className="font-semibold">
          {stock.latest_revenue_year} Q{stock.latest_revenue_quarter}
        </span>
        {stock.latest_revenue_date && (
          <span className="text-xs text-gray-600">
            {stock.latest_revenue_date}
          </span>
        )}
      </div>
    );
  };

  const columns: ColumnsType<Stock> = [
    {
      title: "ID #",
      dataIndex: "id",
      key: "id",
      align: "left",
      width: 80,
    },
    {
      title: "Sector",
      dataIndex: "sector_name",
      key: "sector_name",
      align: "left",
      width: 120,
    },
    {
      title: "Company name",
      dataIndex: "company_name",
      key: "company_name",
      align: "left",
    },
    {
      title: "Ticker symbol",
      dataIndex: "ticker_symbol",
      key: "ticker_symbol",
      align: "left",
      width: 120,
    },
    {
      title: "Financial Source",
      key: "falcon_source",
      align: "left",
      width: 180,
      render: (_, stock: Stock) => renderFalconStatus(stock),
    },
    {
      title: "Latest Quarter",
      key: "latest_quarter",
      align: "left",
      width: 130,
      render: (_, stock: Stock) => renderLatestQuarter(stock),
    },
    {
      title: "Open",
      dataIndex: "open",
      key: "open",
      align: "left",
      width: 100,
    },
    {
      title: "High",
      dataIndex: "high",
      key: "high",
      align: "left",
      width: 100,
    },
    {
      title: "Low",
      dataIndex: "low",
      key: "low",
      align: "left",
      width: 100,
    },
    {
      title: "Last Traded Price",
      dataIndex: "closed",
      key: "closed",
      align: "left",
      width: 150,
    },
    {
      title: "Last Traded date",
      dataIndex: "last_traded_date",
      key: "last_traded_date",
      align: "left",
      width: 150,
    },
    ...(canGenerateSummaries
      ? ([
          {
            title: "Detailed Analysis Summary",
            key: "ai_summary",
            align: "center",
            width: 140,
            render: (_, stock: Stock) => renderSummaryButton("ai", stock),
          },
          {
            title: "Beginner Summary",
            key: "beginner_summary",
            align: "center",
            width: 160,
            render: (_, stock: Stock) =>
              renderSummaryButton("beginner", stock),
          },
        ] as ColumnsType<Stock>)
      : []),
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 350,
      render: (_, stock: Stock) => (
        <div className="flex text-white gap-2 justify-center">
          <Button
            id={stock.id.toString()}
            variant="primary"
            fontSize="small"
            onClick={handleView}
          >
            View
          </Button>
          <Button
            id="edit"
            variant="primary"
            fontSize="small"
            onClick={(e: any) => toggle(e, stock)}
          >
            Edit
          </Button>
          <Button
            id="delete"
            variant="error"
            fontSize="small"
            onClick={(e: any) => toggle(e, stock)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    getSockMarkets();
    getOtherMetrics();
    getSectors();
    getCompanyQuarters();
  }, []);

  const { exportToExcel } = useExportToExcel(stockMarkets, selectedMarket);

  useEffect(() => {
    if (!stockMarketLoading && stockMarkets) {
      const marketId = selectedMarket || stockMarkets[0].id;
      // setSelectedMarket(marketId);
      fetchStocks(marketId);
    }
  }, [stockMarkets, stockMarketLoading]);

  useEffect(() => {
    if (stocks && !stockLoading) {
      setFilteredStock(stocks);
    }
  }, [stocks, stockLoading]);

  useEffect(() => {
    if (!metricsLoading && metrics) {
      toggleModal({
        ...openModal,
        ...metrics.map((m: any) => ({ [m.name]: false })),
      });
      let intialOpts = [
        { label: "Add New", id: "new" },
        { label: "Bulk Upload Stocks", id: "stocks" },
      ];
      setOpts([
        ...intialOpts,
        ...metrics.map((m: any) => ({
          label: `Bulk Upload ${captialCase(m)}`,
          id: m.replace(/_/g, "-"),
        })),
      ]);
    }
  }, [metricsLoading, metrics]);

  const handleMarketSelection = (e: any) => {
    const marketId = e.target.value;
    setSelectedMarket(marketId);
    dispatch({ type: UPDATE_SELECTED_MARKET_ID, payload: marketId });
    fetchStocks(marketId);
  };

  const fetchStocks = (marketId: any) => {
    console.log("fetchStocks", marketId);
    getStocks({ marketId, asMap: false });
  };

  return (
    <div className="flex w-full h-auto min-h-full p-8 flex-col bg-white">
      <Loader show={stockMarketLoading || stockLoading} />
      <DeleteModal
        id="delete"
        data={selectedData}
        status={openModal.delete}
        onClose={toggle}
        onSuccess={() => fetchStocks(selectedMarket)}
      />
      <AddEditModal
        type={openModal.new ? "new" : "edit"}
        heading={openModal.new ? "Add New Stock" : "Edit Stock"}
        stockMarkets={stockMarkets}
        sectors={sectors}
        quarters={quarters}
        data={selectedData}
        status={openModal.new || openModal.edit}
        onClose={toggle}
        onSuccess={() => fetchStocks(selectedMarket)}
      />
      <BulkUploadModal
        name={bulkUploadName}
        data={selectedData}
        status={isBulkUpload}
        onClose={toggle}
        onSuccess={() => fetchStocks(selectedMarket)}
      />

      <div className="flex w-full mb-8 text-white gap-4 items-center">
        {stockMarkets && stockMarkets.length ? (
          <select
            onChange={handleMarketSelection}
            className="text-black border p-2 rounded"
          >
            {stockMarkets.map((market: any) => (
              <option
                selected={selectedMarket == market.id ? true : false}
                key={market.id}
                value={market.id}
              >
                {market.market_name}
              </option>
            ))}
          </select>
        ) : (
          <div className="flex">No Stock markets available. Add new + </div>
        )}

        <div className="flex gap-2">
          {(role_name === "admin" ||
            isAllowed([KEYS.EXPORT_TABLE_OPTION], allowed)) && (
            <Button
              onClick={() =>
                exportToExcel(
                  searchFilteredStocks.length > 0
                    ? searchFilteredStocks
                    : filteredStocks,
                )
              }
              fontSize="small"
              variant="success"
            >
              Export to Excel
            </Button>
          )}
          {canGenerateMarketSummaries && (
          <Button
            onClick={() => {
              const selectedMarketData = stockMarkets?.find(
                (market: any) => market.id == selectedMarket,
              );
              generateAISummaries({
                stock_market_id: selectedMarket,
                short_name: selectedMarketData?.short_name,
              });
            }}
            fontSize="small"
            variant="primary"
            disabled={aiSummaryLoading || isSummaryJobRunning("ai")}
          >
            {summaryJobButtonLabel(
              "ai",
              aiSummaryLoading,
              "Generate AI Summaries",
            )}
          </Button>
          )}
          {canGenerateMarketSummaries && (
          <Button
            onClick={() => {
              const selectedMarketData = stockMarkets?.find(
                (market: any) => market.id == selectedMarket,
              );
              generateBeginnerSummaries({
                stock_market_id: selectedMarket,
                short_name: selectedMarketData?.short_name,
                baseCurrency: selectedMarketData?.currency,
              });
            }}
            fontSize="small"
            variant="primary"
            disabled={beginnerSummaryLoading || isSummaryJobRunning("beginner")}
          >
            {summaryJobButtonLabel(
              "beginner",
              beginnerSummaryLoading,
              "Generate Beginner Summaries",
            )}
          </Button>
          )}
          <div className="relative z-10">
            <ButtonWithDropdown
              fontSize="small"
              options={opts}
              onClick={(e: any) => toggle(e, { selectedMarket })}
              variant="primary"
            >
              Actions
            </ButtonWithDropdown>
          </div>
        </div>
      </div>

      {canGenerateMarketSummaries && visibleSummaryJobs.length > 0 && (
        <div className="mb-4 flex flex-col gap-2">
          {visibleSummaryJobs.map(renderSummaryJobProgress)}
        </div>
      )}

      <div className="mb-4 flex items-center gap-3 relative z-0">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search company name or ticker symbol..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Clear
          </button>
        )}
        {searchTerm && (
          <span className="text-sm text-gray-600">
            Found {searchFilteredStocks.length} of {filteredStocks?.length || 0}
          </span>
        )}
      </div>

      <div className="relative z-0">
        <Table<Stock>
          columns={columns}
          dataSource={searchFilteredStocks}
          rowKey="id"
          loading={stockLoading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          rowHoverable={true}
          bordered={true}
          size="middle"
          nullText="No data available"
          className="shadow-sm"
        />
      </div>
    </div>
  );
};

export default StockMarketData;
